import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Drawer,
  Input,
  Modal,
  Space,
  Spin,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  DownloadOutlined,
  FileTextOutlined,
  LockOutlined,
  MailOutlined,
  MoreOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import { adminAPI, reportAPI, subscriptionAPI } from '../services/api';
import { useResponsive } from '../contexts/ResponsiveContext';
import MobileSectionCard from './mobile/MobileSectionCard';
import type { DoseResult, RemedialDoseResult } from '../types';
import { getDrugLabel } from '../constants/drugs';
import { formatErrorMessage } from '../utils/errorHandler';
import { normalizePkpdResults } from '../utils/pkpdResults';

const { Title, Text } = Typography;

interface ReportPreviewProps {
  calculationId: string;
  doseResult: DoseResult | RemedialDoseResult | null;
  customInterpretation?: string;
  patientCode?: string;
  drugName?: string;
  adminReplay?: boolean;
}

const buildSelectedRegimen = (doseResult: DoseResult | RemedialDoseResult | null) => {
  if (!doseResult) return undefined;
  if ('all_candidates' in doseResult) {
    const selectedCandidate = doseResult.all_candidates.find(
      (candidate) => candidate.id === doseResult.selected_candidate_id,
    ) || doseResult.all_candidates[0];
    const reportIds = new Set(doseResult.report_candidate_ids || [selectedCandidate?.id].filter(Boolean) as string[]);
    return {
      primary_method: doseResult.primary_method,
      remedial_selected_candidate: selectedCandidate,
      remedial_report_candidates: doseResult.all_candidates.filter((candidate) => reportIds.has(candidate.id)),
      remedial_selected_candidate_id: selectedCandidate?.id,
      remedial_strategy_view: doseResult.strategy_view,
    };
  }
  const selectedRegimen = {
    report_has_selected_regimen: doseResult.report_has_selected_regimen === true,
    report_selected_regimen: doseResult.report_selected_regimen ?? null,
    primary_method: doseResult.primary_method,
    administration_mode: doseResult.administration_mode,
    dose: doseResult.recommended_dose_mg,
    dose_kg: doseResult.recommended_dose_mg_kg,
    interval: doseResult.dosing_interval_h,
    infusion_h: doseResult.infusion_duration_h,
    infusion_duration_source: doseResult.infusion_duration_source,
    maintenance_daily_dose_mg: doseResult.maintenance_daily_dose_mg,
    maintenance_rate_mg_h: doseResult.maintenance_rate_mg_h,
    predicted_css: doseResult.predicted_css,
    bag_interval_h: doseResult.bag_interval_h,
    recommended_loading_dose_mg: doseResult.recommended_loading_dose_mg,
    recommended_loading_dose_mg_kg: doseResult.recommended_loading_dose_mg_kg,
    auc24: doseResult.predicted_auc24,
    predicted_cmin: doseResult.predicted_cmin,
    predicted_cmax: doseResult.predicted_cmax,
    evaluation: doseResult.evaluation,
    pkpd_results: normalizePkpdResults(doseResult.pkpd_results),
    // Keep the measured/original regimen alongside the selected optimized
    // regimen so report rendering can show both assessments explicitly.
    original_regimen: doseResult.original_regimen,
    original_regimen_pkpd: normalizePkpdResults(doseResult.original_regimen_pkpd),
    safety_check: doseResult.safety_check,
    simulation_data: doseResult.simulation_data,
  };
  const formulaResult = doseResult as any;
  if (formulaResult.pk_formula_selected_regimen || formulaResult.pk_formula_original_regimen) {
    const formulaSelected = formulaResult.pk_formula_selected_regimen || null;
    const formulaOriginal = formulaResult.pk_formula_original_regimen || null;
    // 未选择候选方案时，报告主方案必须回到原始给药方案，不能沿用后端候选列表第一项。
    const activeFormulaRegimen = formulaSelected || formulaOriginal;
    return {
      ...selectedRegimen,
      dose: activeFormulaRegimen?.dose_mg ?? selectedRegimen.dose,
      dose_kg: activeFormulaRegimen?.dose_mg_kg ?? selectedRegimen.dose_kg,
      interval: activeFormulaRegimen?.interval_h ?? selectedRegimen.interval,
      infusion_h: activeFormulaRegimen?.infusion_h ?? selectedRegimen.infusion_h,
      predicted_cmin: activeFormulaRegimen?.predicted_cmin ?? selectedRegimen.predicted_cmin,
      predicted_cmax: activeFormulaRegimen?.predicted_cmax ?? selectedRegimen.predicted_cmax,
      simulation_data: formulaSelected?.simulation_data ?? formulaOriginal?.simulation_data ?? selectedRegimen.simulation_data,
      pk_formula_selected_regimen: formulaSelected,
      pk_formula_selected_pkpd: formulaResult.pk_formula_selected_pkpd || null,
      pk_formula_original_regimen: formulaOriginal,
      pk_formula_original_pkpd: formulaResult.pk_formula_original_pkpd || null,
    };
  }
  return selectedRegimen;
};

const upgradeTooltip = '当前套餐不支持导出报告，请升级后使用';

const ReportPreview: React.FC<ReportPreviewProps> = ({
  calculationId,
  doseResult,
  customInterpretation,
  patientCode,
  drugName,
  adminReplay = false,
}) => {
  const { isMobileShell } = useResponsive();
  const standardDoseResult = doseResult && !('all_candidates' in doseResult)
    ? doseResult as DoseResult
    : null;
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [loading, setLoading] = useState(true);
  const [html, setHtml] = useState('');
  const [error, setError] = useState('');
  const [canExport, setCanExport] = useState(true);
  const [actionDrawerOpen, setActionDrawerOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  useEffect(() => {
    const loadExportCapability = async () => {
      try {
        const data = await subscriptionAPI.getFeatures();
        setCanExport(Boolean(data.features?.report_export));
      } catch {
        setCanExport(false);
      }
    };

    loadExportCapability();
  }, []);

  useEffect(() => {
    const loadReport = async () => {
      try {
        setLoading(true);
        const selectedRegimen = buildSelectedRegimen(doseResult);
        const generateReport = adminReplay ? adminAPI.replayReportGenerate : reportAPI.generate;
        const reportHtml = await generateReport(
          calculationId,
          customInterpretation ?? standardDoseResult?.clinical_interpretation,
          selectedRegimen,
          standardDoseResult?.combined_curve,
          standardDoseResult?.tdm_points,
          standardDoseResult?.regimen_history,
          standardDoseResult?.tdm_data,
        );
        setHtml(reportHtml);
        setError('');
      } catch (err) {
        setError(formatErrorMessage(err, '报告生成失败'));
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, [adminReplay, calculationId, customInterpretation, doseResult]);

  useEffect(() => {
    if (!html || !iframeRef.current) {
      return undefined;
    }

    const iframe = iframeRef.current;
    const updateHeight = () => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (doc?.body) {
          iframe.style.height = `${Math.max(doc.body.scrollHeight + 40, isMobileShell ? 720 : 600)}px`;
        }
      } catch {
        iframe.style.height = isMobileShell ? '78dvh' : '600px';
      }
    };

    iframe.addEventListener('load', updateHeight);
    return () => iframe.removeEventListener('load', updateHeight);
  }, [html, isMobileShell]);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 1200);
  };

  const handleDownload = () => {
    const filePrefix = patientCode || 'PreciDose';
    const fileName = `${filePrefix}-${getDrugLabel(drugName)}-个体化给药报告`;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.document.title = fileName;
    setTimeout(() => printWindow.print(), 1200);
  };

  const handleSendEmail = async () => {
    if (!emailAddress || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddress)) {
      message.warning('请输入有效邮箱地址');
      return;
    }

    try {
      setEmailSending(true);
      const selectedRegimen = buildSelectedRegimen(doseResult);
      await reportAPI.sendEmail(
        calculationId,
        emailAddress,
        undefined,
        customInterpretation ?? standardDoseResult?.clinical_interpretation,
        selectedRegimen,
        standardDoseResult?.combined_curve,
        standardDoseResult?.tdm_points,
        standardDoseResult?.regimen_history,
        standardDoseResult?.tdm_data,
      );
      message.success(`报告已发送至 ${emailAddress}`);
      setEmailModalOpen(false);
      setEmailAddress('');
      setActionDrawerOpen(false);
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '邮件发送失败');
    } finally {
      setEmailSending(false);
    }
  };

  const exportDisabledButtons = (
    <>
      <Tooltip title={upgradeTooltip}>
        <Button icon={<LockOutlined />} disabled>下载报告</Button>
      </Tooltip>
      <Tooltip title={upgradeTooltip}>
        <Button icon={<LockOutlined />} disabled>打印报告</Button>
      </Tooltip>
      <Tooltip title={upgradeTooltip}>
        <Button icon={<LockOutlined />} disabled>发送邮件</Button>
      </Tooltip>
    </>
  );

  const exportActionButtons = (
    <>
      <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownload}>
        下载报告
      </Button>
      <Button icon={<PrinterOutlined />} onClick={handlePrint}>
        打印报告
      </Button>
      <Button icon={<MailOutlined />} onClick={() => setEmailModalOpen(true)}>
        发送邮件
      </Button>
    </>
  );

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text>正在生成报告预览...</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return <Alert type="error" message="报告生成失败" description={error} showIcon />;
  }

  const previewContent = (
    <iframe
      ref={iframeRef}
      srcDoc={html}
      title="PreciDose Report"
      sandbox="allow-scripts allow-same-origin"
      style={{
        width: '100%',
        minHeight: isMobileShell ? '78dvh' : 600,
        border: 'none',
        background: '#fff',
      }}
    />
  );

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isMobileShell ? 'flex-start' : 'center',
          flexDirection: isMobileShell ? 'column' : 'row',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div>
          <Title level={5} style={{ margin: 0 }}>
            <FileTextOutlined style={{ marginRight: 8 }} />
            给药方案报告
          </Title>
          {isMobileShell ? (
            <Text type="secondary">移动端操作已收纳至底部操作面板，报告正文保持原模板输出。</Text>
          ) : null}
        </div>

        {isMobileShell ? (
          <div style={{ display: 'flex', gap: 10, width: '100%' }}>
            {canExport ? (
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                block
                onClick={handleDownload}
              >
                下载报告
              </Button>
            ) : (
              <Tooltip title={upgradeTooltip}>
                <Button icon={<LockOutlined />} block disabled>
                  下载报告
                </Button>
              </Tooltip>
            )}
            <Button icon={<MoreOutlined />} onClick={() => setActionDrawerOpen(true)}>
              更多操作
            </Button>
          </div>
        ) : (
          <Space>{canExport ? exportActionButtons : exportDisabledButtons}</Space>
        )}
      </div>

      {isMobileShell ? (
        <MobileSectionCard bodyStyle={{ padding: 0, overflow: 'hidden' }}>
          {previewContent}
        </MobileSectionCard>
      ) : (
        <Card bodyStyle={{ padding: 0, overflow: 'hidden' }}>
          {previewContent}
        </Card>
      )}

      <Drawer
        open={actionDrawerOpen}
        onClose={() => setActionDrawerOpen(false)}
        placement="bottom"
        title="报告操作"
        height="auto"
        styles={{
          content: {
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            overflow: 'hidden',
          },
        }}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {canExport ? (
            <>
              <Button type="primary" icon={<DownloadOutlined />} block onClick={handleDownload}>
                下载报告
              </Button>
              <Button icon={<PrinterOutlined />} block onClick={handlePrint}>
                打印报告
              </Button>
              <Button icon={<MailOutlined />} block onClick={() => setEmailModalOpen(true)}>
                发送到邮箱
              </Button>
            </>
          ) : (
            <>
              <Button icon={<LockOutlined />} block disabled>下载报告</Button>
              <Button icon={<LockOutlined />} block disabled>打印报告</Button>
              <Button icon={<LockOutlined />} block disabled>发送到邮箱</Button>
            </>
          )}
        </Space>
      </Drawer>

      <Modal
        title="发送报告到邮箱"
        open={emailModalOpen}
        onOk={handleSendEmail}
        onCancel={() => {
          setEmailModalOpen(false);
          setEmailAddress('');
        }}
        confirmLoading={emailSending}
        okText="发送"
        cancelText="取消"
        width={isMobileShell ? 'calc(100vw - 32px)' : 520}
      >
        <div style={{ marginBottom: 8 }}>收件人邮箱</div>
        <Input
          type="email"
          placeholder="请输入邮箱地址"
          value={emailAddress}
          onChange={(event) => setEmailAddress(event.target.value)}
          onPressEnter={handleSendEmail}
        />
        <div style={{ marginTop: 8, color: '#8c8c8c', fontSize: 12 }}>
          报告将以 HTML 附件形式发送，正文模板与页面端保持一致。
        </div>
      </Modal>
    </div>
  );
};

export default ReportPreview;
